// Data + round casting for Sound Farm: Barnyard Band.
//
// Ten animal friends (voice clips at asf_<id>.mp3, live art as real-alpha
// cutouts asf_cutout_<id>.png, showtime stickers asf_portrait_<id>.png)
// perform on empty painted farm stages. Each round a mystery singer auditions;
// the child finds them among the live animals on stage and they join the band
// on the front riser. Ported from AnimalFarmModels.swift.

const ASSET_BASE = "/assets/games/animal-sound-farm";

export const BAND_SIZE = 3;

// MARK: - Animals

export const FARM_ANIMALS = [
  { id: "cow", name: "Cow", sound: "Moooooo!", soundLabel: "moo" },
  { id: "pig", name: "Pig", sound: "Oink, oink!", soundLabel: "oink" },
  { id: "sheep", name: "Sheep", sound: "Baaaaaa!", soundLabel: "baa" },
  { id: "horse", name: "Horse", sound: "Neighhhhh!", soundLabel: "neigh" },
  { id: "chicken", name: "Chicken", sound: "Cluck, cluck, cluck!", soundLabel: "cluck" },
  { id: "duck", name: "Duck", sound: "Quack, quack!", soundLabel: "quack" },
  { id: "goat", name: "Goat", sound: "Maaaaaa!", soundLabel: "maa" },
  { id: "donkey", name: "Donkey", sound: "Hee-haw! Hee-haw!", soundLabel: "hee-haw" },
  { id: "turkey", name: "Turkey", sound: "Gobble, gobble, gobble!", soundLabel: "gobble" },
  { id: "rooster", name: "Rooster", sound: "Cock-a-doodle-doo!", soundLabel: "cock-a-doodle-doo" },
];

export const cutoutAsset = (id) => `${ASSET_BASE}/asf_cutout_${id}.png`;
export const portraitAsset = (id) => `${ASSET_BASE}/asf_portrait_${id}.png`;
export const soundUrl = (id) => `${ASSET_BASE}/sounds/${id}.mp3`;

const animalById = Object.fromEntries(FARM_ANIMALS.map((animal) => [animal.id, animal]));
export const animal = (id) => animalById[id] || null;

// MARK: - Stage spots
//
// Percent coordinates of the stage artwork. x/y anchor the animal's FEET.
// scale is the sprite height as a fraction of the stage height (smaller =
// farther away).

const spot = (x, y, scale) => ({ x, y, scale });

// The control dock (feedback bubble + replay speaker) sits centred at the
// stage's bottom, so the front row deliberately leaves that zone empty —
// no singer may stand where the dock would cover them.

// Portrait (9:16) — dock zone is wide on narrow screens; front row sits
// higher and off-centre.
const STANDING = [
  spot(30, 52, 0.1), spot(50, 51, 0.1), spot(70, 52, 0.105),
  spot(25, 64, 0.12), spot(50, 65, 0.125), spot(75, 64, 0.12),
  spot(24, 74, 0.13), spot(50, 73, 0.125), spot(76, 74, 0.13),
];
// Landscape (16:9) — the dock sits bottom-RIGHT (x ~78+), and the band riser
// slots sit at 29/50/71%; front-row spots thread between both.
const STANDING_WIDE = [
  spot(14, 66, 0.17), spot(28, 64, 0.16), spot(42, 66, 0.17),
  spot(56, 64, 0.16), spot(70, 66, 0.17), spot(84, 64, 0.16),
  spot(10, 77, 0.19), spot(40, 78, 0.19), spot(62, 77, 0.19),
];

// MARK: - Stages

export const ART_ASPECT_PORTRAIT = { width: 1080, height: 1920 };
export const ART_ASPECT_LANDSCAPE = { width: 1920, height: 1080 };

export const FARM_STAGES = [
  { id: "day-meadow", name: "Sunny Meadow", image: "asf_stage_day", ambience: "pollen" },
  { id: "sunset-meadow", name: "Sunset Show", image: "asf_stage_sunset", ambience: "pollen" },
  { id: "night-lights", name: "Starlight Show", image: "asf_stage_night", ambience: "fireflies" },
  { id: "winter-show", name: "Snowy Show", image: "asf_stage_winter", ambience: "snow" },
].map((stage) => ({
  ...stage,
  standingSpots: STANDING,
  standingSpotsWide: STANDING_WIDE,
}));

export const stageImage = (stage, landscape) =>
  `${ASSET_BASE}/${stage.image}${landscape ? "_wide" : ""}.jpg`;
export const stageStanding = (stage, landscape) =>
  (landscape ? stage.standingSpotsWide : stage.standingSpots);
export const artAspect = (landscape) =>
  (landscape ? ART_ASPECT_LANDSCAPE : ART_ASPECT_PORTRAIT);

// MARK: - Shuffle

export const shuffle = (items, random = Math.random) => {
  const result = [...items];
  for (let index = result.length - 1; index > 0; index -= 1) {
    const swapIndex = Math.floor(random() * (index + 1));
    [result[index], result[swapIndex]] = [result[swapIndex], result[index]];
  }
  return result;
};

/** A fresh stage order that never opens with the stage just played. */
export const shuffledStages = (avoidingFirstId = null, random = Math.random) => {
  const deck = shuffle(FARM_STAGES, random);
  if (deck.length > 1 && deck[0].id === avoidingFirstId) {
    [deck[0], deck[1]] = [deck[1], deck[0]];
  }
  return deck;
};

// MARK: - Round casting
//
// A CastMember records the animal + the standing slot INDEX so the actual
// coordinates resolve per orientation at render time (a member keeps its slot
// when the device rotates). Ported from FarmData.makeRound.

/**
 * Resolve a cast member's spot for the given orientation.
 */
export const memberSpot = (member, stage, landscape) =>
  stageStanding(stage, landscape)[member.standingIndex];

/**
 * Build the on-stage cast for a round. The hunt grows with the show: more
 * animals as the band fills and shows complete. Band members are never in the
 * cast; the same animal never sings twice in a row.
 */
export const makeRound = ({
  stage,
  bandCount = 0,
  showsCompleted = 0,
  excluding = [],
  previousTargetId = null,
  random = Math.random,
}) => {
  const recruited = new Set(excluding.map((item) => item.id));
  const pool = shuffle(FARM_ANIMALS.filter((item) => !recruited.has(item.id)), random);
  if (pool.length > 1 && pool[0].id === previousTargetId) {
    [pool[0], pool[1]] = [pool[1], pool[0]];
  }

  const castSize = Math.min(5 + bandCount + Math.min(showsCompleted, 2), 8, pool.length);

  const standIdx = shuffle(
    Array.from({ length: stage.standingSpots.length }, (_, i) => i),
    random
  ).slice(0, castSize);

  const members = standIdx.map((slot, index) => ({
    animal: pool[index],
    standingIndex: slot,
  }));

  const cast = shuffle(members, random);
  // pool[0] took the first standing slot (castSize >= 1), so the target is
  // always somewhere in the cast — a fair hunt.
  return { cast, target: pool[0] };
};

// MARK: - Geometry (feet-anchored, perspective-scaled)

/**
 * The rect the stage art occupies when it fills a container (object-fit:cover,
 * centered — may extend past the edges). Mirrors iOS filledRect.
 */
export const filledRect = (width, height, landscape) => {
  const aspect = artAspect(landscape);
  const scale = Math.max(width / aspect.width, height / aspect.height);
  const w = aspect.width * scale;
  const h = aspect.height * scale;
  return { minX: (width - w) / 2, minY: (height - h) / 2, width: w, height: h };
};

/** Sprite visual center (px) for a spot inside the content rect. */
export const spriteCenter = (spotDef, content) => {
  const h = content.height * spotDef.scale;
  const rawX = content.minX + content.width * spotDef.x / 100;
  const rawFootY = content.minY + content.height * spotDef.y / 100;
  // The stage art fills its box with object-fit:cover, so on containers whose
  // aspect differs from the art it gets cropped (content.minX/minY < 0).
  // Without clamping, the outermost spots land past the stage's overflow:hidden
  // edge and the animal is sliced off — on the sides for narrow containers, or
  // below the bottom for the low front-row spots on near-square/tall ones. Keep
  // the whole sprite inside the visible stage. Half the button width (0.95h) is
  // ~0.475h; round to 0.5h for the drop shadow, plus a small breathing margin.
  const viewWidth = content.width + content.minX * 2;
  const viewHeight = content.height + content.minY * 2;
  const halfSprite = h * 0.5;
  const margin = h * 0.06;
  const x = Math.min(
    Math.max(rawX, halfSprite + margin),
    viewWidth - halfSprite - margin
  );
  // Keep feet above the bottom edge and the head below the top; if the sprite
  // is taller than the stage, favour keeping the feet on screen.
  const maxFootY = viewHeight - margin;
  const minFootY = Math.min(h + margin, maxFootY);
  const footY = Math.min(Math.max(rawFootY, minFootY), maxFootY);
  return { x, y: footY - h / 2 };
};

export const riserSlotCenter = (index, content) => {
  // Portrait screens keep the dock bottom-right too, but it spans a larger
  // share of a narrow stage — pack the band toward the left so the third
  // member never ends up under the speaker.
  const landscape = content.width > content.height;
  const centerX = landscape ? 0.5 : 0.38;
  const step = landscape ? 0.21 : 0.22;
  return {
    x: content.minX + content.width * (centerX + (index - 1) * step),
    y: content.minY + content.height * (0.965 - 0.115),
  };
};
